import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import { cn } from "@/lib/utils";
import { CalendarDays, Check, Clock, Info } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cardClassName, pillPrimaryButtonClassName } from "./assistantClasses";
import formatDuration from "./formatDuration";

type AgendaTimeSlotsResponseProps = {
    timeSlots: AgendaTimeSlot[];
    /**
     * For each slot, in the same order: the message a click sends (step-based contract), as if the
     * user had typed it. Absent: the slot is only shown.
     */
    replies?: (string | undefined)[];
    /** A later message exists: the slots are no longer offered, they are only shown. */
    isPast?: boolean;
};

/**
 * One free slot, the day written once: "mer. 7 oct. · 14:00 → 15:00". A slot that spans two
 * days gives both days.
 */
export const formatTimeSlot = (
    start: Date,
    end: Date,
    language: string,
): string => {
    const day = (date: Date) =>
        date.toLocaleDateString(language, {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
    const hour = (date: Date) =>
        date.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });
    const sameDay = start.toDateString() === end.toDateString();

    return sameDay
        ? `${day(start)} · ${hour(start)} → ${hour(end)}`
        : `${day(start)} · ${hour(start)} → ${day(end)} · ${hour(end)}`;
};

const pillClassName =
    "inline-flex h-11 w-fit max-w-full items-center gap-2 rounded-full border px-4 text-left";

/**
 * The free slots of an answer, one column per day. Offered by the assistant (`replies`), a slot is
 * picked, then « Créer un rendez-vous » sends its message: the assistant then proposes the meeting —
 * nothing is created by the click.
 */
const AgendaTimeSlotsResponse = ({
    timeSlots,
    replies,
    isPast = false,
}: AgendaTimeSlotsResponseProps) => {
    const { t, i18n } = useTranslation();
    const { onAnswerQuestion } = useVistassistantMail();
    const slots = timeSlots.map((slot, index) => ({
        index,
        start: new Date(slot.startDate),
        end: new Date(slot.endDate),
        reply: isPast || !onAnswerQuestion ? undefined : replies?.[index],
    }));
    const [selected, setSelected] = useState(
        () => slots.find((slot) => slot.reply)?.index,
    );
    const isOffered = slots.some((slot) => slot.reply);
    const days = slots.reduce<{ key: string; slots: typeof slots }[]>(
        (groups, slot) => {
            const key = slot.start.toDateString();
            const group = groups.find((day) => day.key === key);
            if (group) group.slots.push(slot);
            else groups.push({ key, slots: [slot] });
            return groups;
        },
        [],
    );

    if (slots.length === 0) return null;

    const language = i18n.language;
    const hour = (date: Date) =>
        date.toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });
    const fullDay = (date: Date) =>
        date.toLocaleDateString(language, { day: "numeric", month: "long" });
    const starts = slots.map((slot) => slot.start.getTime());
    const first = new Date(Math.min(...starts));
    const last = new Date(Math.max(...starts));
    const sameMonth =
        first.getMonth() === last.getMonth() &&
        first.getFullYear() === last.getFullYear();
    const range =
        first.toDateString() === last.toDateString()
            ? fullDay(first)
            : t("vistassistant.slots.range", {
                  from: sameMonth
                      ? first.toLocaleDateString(language, { day: "numeric" })
                      : fullDay(first),
                  to: fullDay(last),
              });
    const title = t("vistassistant.slots.title", { range });
    const chosen = slots.find((slot) => slot.index === selected);

    return (
        <section
            aria-label={title}
            className={cn(
                cardClassName,
                "flex w-full flex-col gap-4 rounded-[20px] p-4",
            )}
        >
            <h3 className="flex items-center gap-2.5 text-lg font-semibold text-primary-darkest">
                <CalendarDays
                    aria-hidden
                    className="size-5 flex-none text-primary-dark"
                    strokeWidth={1.75}
                />
                {title}
            </h3>
            <div
                aria-label={isOffered ? title : undefined}
                className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-x-4 gap-y-4"
                role={isOffered ? "radiogroup" : undefined}
            >
                {days.map((day) => (
                    <div key={day.key} className="flex flex-col gap-2">
                        <p className="text-base font-semibold text-assistant-muted first-letter:uppercase">
                            {day.slots[0].start.toLocaleDateString(language, {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                            })}
                        </p>
                        {day.slots.map((slot) => {
                            const isSelected =
                                !!slot.reply && slot.index === selected;
                            const content = (
                                <>
                                    {isSelected ? (
                                        <Check
                                            aria-hidden
                                            className="size-5 flex-none"
                                            strokeWidth={1.75}
                                        />
                                    ) : (
                                        <Clock
                                            aria-hidden
                                            className="size-5 flex-none text-primary-darkest"
                                            strokeWidth={1.75}
                                        />
                                    )}
                                    <span className="whitespace-nowrap text-base font-semibold">
                                        {hour(slot.start)} – {hour(slot.end)}
                                    </span>
                                    <span
                                        className={cn(
                                            "whitespace-nowrap text-sm",
                                            isSelected
                                                ? "text-white"
                                                : "text-assistant-muted",
                                        )}
                                    >
                                        {formatDuration(
                                            slot.start,
                                            slot.end,
                                            t,
                                        )}
                                    </span>
                                </>
                            );

                            return slot.reply ? (
                                <button
                                    key={slot.index}
                                    aria-checked={isSelected}
                                    className={cn(
                                        pillClassName,
                                        "transition-colors",
                                        isSelected
                                            ? "border-primary-dark bg-primary-dark text-white"
                                            : "border-assistant-outline bg-white text-primary-darkest hover:bg-assistant-chip",
                                    )}
                                    onClick={() => setSelected(slot.index)}
                                    role="radio"
                                    type="button"
                                >
                                    {content}
                                </button>
                            ) : (
                                <span
                                    key={slot.index}
                                    className={cn(
                                        pillClassName,
                                        "border-assistant-outline bg-white text-primary-darkest",
                                    )}
                                >
                                    {content}
                                </span>
                            );
                        })}
                    </div>
                ))}
            </div>
            <p className="flex items-start gap-2 text-sm text-assistant-muted">
                <Info aria-hidden className="mt-0.5 size-4 flex-none" />
                {t("vistassistant.slots.hint")}
            </p>
            {isOffered && (
                <div className="flex flex-wrap gap-2">
                    <button
                        className={pillPrimaryButtonClassName}
                        disabled={!chosen?.reply}
                        onClick={() =>
                            chosen?.reply && onAnswerQuestion?.(chosen.reply)
                        }
                        type="button"
                    >
                        <CalendarDays aria-hidden strokeWidth={1.75} />
                        {chosen &&
                            t("vistassistant.slots.create", {
                                day: chosen.start.toLocaleDateString(language, {
                                    weekday: "long",
                                }),
                                time: hour(chosen.start),
                            })}
                    </button>
                </div>
            )}
        </section>
    );
};

export default AgendaTimeSlotsResponse;
